import React from "react";
import { ChartGantt } from "lucide-react";
import { ActionButton } from "./ResultsPrimitives.jsx";
import ReportsDisabledActionHoverCard from "./ReportsDisabledActionHoverCard.jsx";
import ReportEmailActions from "./ReportEmailActions.jsx";
import { useUserUiPreferences } from "../hooks/useUserUiPreferences.js";
import { downloadDispatchGanttReport, markExportDocumentWindowFailed, openExportDocumentWindow } from "../lib/exportApi.js";
import { defaultReportEmailItem } from "../utils/reportEmailForm.js";

export default function ReportsDispatchGanttCard({
  dispatchDate = "", dispatchPeriod = "day", hasSnapshot = false,
  isLoading = false, onDispatchPeriodChange, onExported,
}) {
  const { dispatchColorIntensityPercent } = useUserUiPreferences();
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [feedback, setFeedback] = React.useState(null);
  React.useEffect(() => setFeedback(null), [dispatchDate, dispatchPeriod]);
  const disabledReason = !dispatchDate ? "Sélectionnez une date de dispatch."
    : isLoading ? "Chargement du dispatch…"
    : !hasSnapshot ? "Aucun dispatch enregistré pour cette date et cette période."
    : isGenerating ? "Le PDF est en préparation." : "";

  const generate = async () => {
    if (disabledReason) return;
    setIsGenerating(true);
    setFeedback(null);
    const scopeLabel = `${dispatchDate} · ${dispatchPeriod === "evening" ? "Soir" : "Jour"}`;
    let documentWindow;
    try {
      documentWindow = openExportDocumentWindow({
        detail: scopeLabel, eyebrow: "Dispatch visuel", message: "Préparation du Gantt complet et des indicateurs sur une page en légal paysage.",
        preferredWidth: 1344, title: "Préparation du dispatch visuel", windowName: "grain-dispatch-gantt-report",
      });
      await downloadDispatchGanttReport({ dispatchDate, dispatchPeriod, colorIntensityPercent: dispatchColorIntensityPercent, documentWindow });
      onExported?.();
      setFeedback({ error: false, message: `Le dispatch visuel du ${scopeLabel} est prêt dans le navigateur.` });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Le rapport n’a pas pu être généré.";
      if (documentWindow) markExportDocumentWindowFailed(documentWindow, {
        detail: message, eyebrow: "Dispatch visuel", message: "Le rapport n’a pas pu être généré.", title: "Export interrompu",
      });
      setFeedback({ error: true, message });
    } finally {
      setIsGenerating(false);
    }
  };

  return <article className="reports-card" data-dispatch-gantt-report-card>
    <div className="reports-card-header">
      <div className="reports-card-icon"><ChartGantt size={25} aria-hidden="true" /></div>
      <div className="grid gap-0.5"><h2 className="reports-card-title text-slate-950">Dispatch visuel</h2>
        <p className="text-[0.82rem] leading-[1.35] text-slate-600">Le planning de la flotte et ses indicateurs en un document.</p></div>
    </div>
    <div className="reports-card-body">
      <label className="reports-field">
        <span className="reports-label">Période du dispatch</span>
        <select aria-label="Période du dispatch visuel" value={dispatchPeriod} disabled={isGenerating}
          onChange={(event) => onDispatchPeriodChange?.(event.target.value)}
          className="min-h-10 min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle disabled:bg-slate-50 disabled:text-slate-400">
          <option value="day">Jour</option><option value="evening">Soir</option>
        </select>
      </label>
      <p className="text-[0.82rem] leading-5 text-slate-500">PDF légal paysage (14 × 8,5 po), ajusté sur une page.</p>
      {dispatchPeriod === "day" && <p className="text-[0.82rem] leading-5 text-slate-500">
        Si un camion est planifié au Soir, cette période est ajoutée automatiquement sur 20 % du Gantt, avec ses indicateurs et ses listes.
      </p>}
      <div className="reports-card-actions">
        <ReportsDisabledActionHoverCard actionLabel="Générer le PDF" disabledReason={disabledReason} fitContent>
          <ActionButton tone="optimizer" size="sm" className="reports-primary-action"
            onClick={generate} disabled={Boolean(disabledReason)} isLoading={isGenerating} loadingLabel="Génération…">Générer le PDF</ActionButton>
        </ReportsDisabledActionHoverCard>
        <ReportEmailActions
          className="reports-email-actions"
          items={[defaultReportEmailItem("report", "dispatch_gantt", {
            date_mode: dispatchDate ? "fixed" : "today",
            dispatch_period: dispatchPeriod,
            fixed_date: dispatchDate || null,
          })]}
        />
      </div>
    </div>
    {feedback && <p role={feedback.error ? "alert" : "status"} className={`rounded-[4px] border px-3 py-2 text-[0.82rem] ${feedback.error
      ? "border-border-danger-subtle bg-danger-soft text-danger" : "border-border-accent-subtle bg-accent-soft text-accent-strong"}`}>{feedback.message}</p>}
  </article>;
}
